import React from 'react';

const ProjectArchive = () => {
  
  const projects = [
    {
      id: "01",
      title: "GYM SAAS PLATFORM",
      client: "FitPulse Inc.",
      role: "Full Stack Engineering",
      year: "2025",
      desc: "A complete business OS for fitness trainers. Automated scheduling, diet planning, and Stripe payments.",
      image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1470&auto=format&fit=crop",
      link: "https://topfitness-mu.vercel.app",
      tags: ["MERN", "Redux", "Stripe"]
    },
    {
      id: "02",
      title: "SALON MANAGEMENT",
      client: "Ecru Luxe",
      role: "Backend Architecture",
      year: "2024",
      desc: "High-performance CRM for a luxury salon franchise. Handles 500+ daily appointments with zero latency.",
      image: "https://images.unsplash.com/photo-1560066984-35bb85db4810?q=80&w=2574&auto=format&fit=crop",
      link: "https://salon-ecru.vercel.app/",
      tags: ["Node.js", "Redis", "Postgres"]
    },
    {
      id: "03",
      title: "ICE CREAM LOGISTICS",
      client: "Jatra Scoops",
      role: "Frontend Interface",
      year: "2024",
      desc: "Real-time inventory tracking for a multi-outlet ice cream brand. Visualized data with Recharts.",
      image: "https://images.unsplash.com/photo-1501443762994-82bd5dace89a?q=80&w=2070&auto=format&fit=crop",
      link: "https://jatra-theta.vercel.app/",
      tags: ["React", "Firebase", "Tailwind"]
    },
    {
      id: "04",
      title: "NEON E-COMMERCE",
      client: "CyberStreet",
      role: "Headless Shopify",
      year: "2023",
      desc: "A futuristic streetwear store. We decoupled the frontend to achieve a 99/100 Google Lighthouse score.",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?q=80&w=2670&auto=format&fit=crop",
      link: "#",
      tags: ["Next.js", "Shopify API", "Framer"]
    }
  ];

  
  return (
    <div className="min-h-screen bg-[#0f172a] text-white pt-24 pb-24 relative overflow-hidden">
      
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>

      <div className="container mx-auto px-6 relative z-10">
        
        {/* --- HEADER --- */}
        <div className="text-center mb-32">
            <span className="text-[#00F0FF] font-mono text-sm tracking-widest uppercase mb-4 block">
                // Selected Works 2023-2025
            </span>
            <h1 className="text-5xl md:text-8xl font-black font-display text-white mb-6">
                THE <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00F0FF] to-purple-500">VAULT</span>
            </h1>
            <p className="text-gray-400 max-w-xl mx-auto text-lg">
                A curation of digital products engineered for performance, scalability, and business impact.
            </p>
        </div>

        {/* --- PROJECT SHOWCASE --- */}
        <div className="space-y-32">
            
            {projects.map((project, index) => (
                <div key={project.id} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-20 group`}>
                    
                    {/* === IMAGE SECTION (The Visual Hook) === */}
                    <div className="w-full lg:w-3/5 relative">
                        {/* Decorative 'Behind' Box */}
                        <div className={`absolute top-4 ${index % 2 === 0 ? '-right-4' : '-left-4'} w-full h-full border border-[#00F0FF]/30 rounded-2xl z-0 transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2`}></div>
                        
                        {/* The Image Container */}
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl z-10 aspect-[16/10]">
                            <img 
                                src={project.image} 
                                alt={project.title} 
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 filter grayscale-[20%] group-hover:grayscale-0"
                            />
                            {/* Overlay on hover */}
                            <a 
                                href={project.link} 
                                target="_blank"
                                rel="noopener noreferrer"
                                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center cursor-pointer"
                            >
                                <div className="bg-[#00F0FF] text-black px-8 py-3 rounded-full font-bold uppercase tracking-widest hover:scale-105 transition-transform shadow-[0_0_20px_rgba(0,240,255,0.6)]">
                                    Launch Project
                                </div>
                            </a>
                        </div>
                    </div>

                    {/* === TEXT SECTION (The Info) === */}
                    <div className="w-full lg:w-2/5 relative">
                        
                        {/* Huge Number */}
                        <div className="absolute -top-20 -left-10 text-[120px] font-black text-white/5 font-display select-none -z-10">
                            {project.id}
                        </div>

                        <div className="flex flex-col items-start">
                            {/* Tags */}
                            <div className="flex gap-2 mb-6">
                                {project.tags.map(tag => (
                                    <span key={tag} className="text-[10px] font-mono border border-white/10 bg-white/5 px-2 py-1 rounded text-[#00F0FF] uppercase">
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            <h2 className="text-4xl md:text-5xl font-black font-display text-white mb-4 leading-tight">
                                {project.title}
                            </h2>

                            <div className="h-1 w-20 bg-[#00F0FF] mb-6"></div>

                            <h3 className="text-sm font-mono text-gray-400 uppercase tracking-widest mb-4">
                                {project.client} • {project.year}
                            </h3>

                            <p className="text-gray-400 text-lg leading-relaxed mb-8">
                                {project.desc}
                            </p>

                            <a 
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer" 
                                className="text-white border-b border-[#00F0FF] pb-1 hover:text-[#00F0FF] transition-colors text-sm font-bold uppercase tracking-widest"
                            >
                                View Case Study →
                            </a>
                        </div>
                    </div>

                </div>
            ))}

        </div>

        {/* --- BOTTOM CTA --- */}
        <div className="mt-40 text-center">
            <p className="text-gray-500 font-mono text-xs uppercase tracking-widest mb-4">
                // End of Archive
            </p>
            <h2 className="text-3xl font-bold text-white mb-8">
                Ready to build the next big thing?
            </h2>
            <a href="/contact" className="px-10 py-4 bg-[#18181b] border border-white/20 text-white rounded-full hover:bg-white hover:text-black hover:border-white transition-all font-bold uppercase tracking-widest">
                Start a Project
            </a>
        </div>

      </div>
    </div>
  );
};

export default ProjectArchive;